import type { PhotoItem } from "./types";
import { formatDuration } from "./utils/media";

type CompGalleryCardProps = {
  photo: PhotoItem;
  apiBase: string;
  onFullscreen: (photo: PhotoItem) => void;
  onDelete: (id: string) => void;
  isDeleting: boolean;
};

export function CompGalleryCard({
  photo: p,
  apiBase,
  onFullscreen,
  onDelete,
  isDeleting,
}: CompGalleryCardProps) {
  const isVideo = p.media_kind === "video";
  const durationStr = isVideo && p.duration != null ? formatDuration(p.duration) : null;

  return (
    <li className="rounded-2xl bg-[var(--wedding-white)] shadow-md border border-[var(--wedding-blush-strong)] overflow-hidden w-42 hover:shadow-[var(--wedding-shadow-hover)] transition-shadow duration-200">
      <div className="w-full h-42 bg-[var(--wedding-cream-deep)] relative group">
        {isVideo ? (
          <>
            <button
              type="button"
              onClick={() => onFullscreen(p)}
              className="w-full h-full bg-neutral-800 flex items-center justify-center cursor-pointer hover:bg-neutral-700 transition-colors overflow-hidden"
              aria-label="Video abspielen"
            >
              {p.thumbnail_url ? (
                <img
                  src={`${apiBase}${p.thumbnail_url}`}
                  alt=""
                  className="w-full h-full object-cover"
                  loading="lazy"
                />
              ) : null}
            </button>
            {durationStr != null && (
              <span className="absolute bottom-1 right-1 px-1.5 py-0.5 rounded-md bg-black/70 text-white text-xs font-medium">
                {durationStr}
              </span>
            )}
          </>
        ) : (
          <button
            type="button"
            onClick={() => onFullscreen(p)}
            className="w-full h-full object-cover"
            aria-label="Bild anzeigen"
          >
            <img
              src={`${apiBase}${p.thumbnail_url ?? p.url}`}
              alt={p.file_name}
              className="w-full h-full object-cover"
              loading="lazy"
            />
          </button>
        )}
        <div className="absolute top-1 right-1 flex gap-1 opacity-100 lg:opacity-0 lg:group-hover:opacity-100 transition-opacity">
          <a
            href={`${apiBase}/api/photos/${p.id}/download`}
            download={p.file_name}
            className="p-2 rounded-xl bg-black/50 text-white hover:bg-black/70 min-w-[44px] min-h-[44px] flex items-center justify-center touch-manipulation transition-colors"
            title="Herunterladen"
            aria-label="Herunterladen"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
            </svg>
          </a>
          <button
            type="button"
            onClick={() => onFullscreen(p)}
            className="p-2 rounded-xl bg-black/50 text-white hover:bg-black/70 min-w-[44px] min-h-[44px] flex items-center justify-center touch-manipulation transition-colors"
            title={isVideo ? "Abspielen" : "Vollbild anzeigen"}
            aria-label={isVideo ? "Video abspielen" : "Vollbild anzeigen"}
          >
            {isVideo ? (
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24" aria-hidden>
                <path d="M8 5v14l11-7z" />
              </svg>
            ) : (
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5l-5-5m5 5v-4m0 4h-4" />
              </svg>
            )}
          </button>
          {p.owned && (
            <button
              type="button"
              onClick={() => onDelete(p.id)}
              disabled={isDeleting}
              className="p-2 rounded-xl bg-red-600/90 text-white hover:bg-red-700 disabled:opacity-50 min-w-[44px] min-h-[44px] flex items-center justify-center touch-manipulation transition-colors"
              title="Löschen"
              aria-label="Löschen"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 6h18M8 6V4a2 2 0 012-2h4a2 2 0 012 2v2m3 0v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6h14zM10 11v6m4-6v6" />
              </svg>
            </button>
          )}
        </div>
      </div>
      <div className="p-2.5 flex flex-col gap-1 border-t border-[var(--wedding-blush)]">
        <p className="text-sm text-[var(--wedding-ink-muted)] truncate" title={p.file_name}>
          {p.file_name}
        </p>
      </div>
    </li>
  );
}
